/**
 * Date formatting utilities
 * Converts between ISO date strings and French DD/MM/YYYY format
 * Follows functional programming principles: pure functions, immutability, no side effects
 */

/**
 * Formats an ISO date string to French format DD/MM/YYYY
 * Returns empty string for invalid input
 */
export const formatDateFr = (isoDate: string): string => {
  if (!isoDate || typeof isoDate !== 'string') {
    return '';
  }

  const date = new Date(isoDate);
  
  if (isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Parses a French date string DD/MM/YYYY to ISO format YYYY-MM-DD
 * Returns empty string for invalid input
 */
export const parseDateFr = (frenchDate: string): string => {
  if (!frenchDate || typeof frenchDate !== 'string') {
    return '';
  }

  // Match DD/MM/YYYY or D/M/YYYY format
  const match = frenchDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  
  if (!match) {
    return '';
  }

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  // Validate date
  const date = new Date(year, month - 1, day);
  
  if (
    isNaN(date.getTime()) ||
    date.getDate() !== day ||
    date.getMonth() !== month - 1 ||
    date.getFullYear() !== year
  ) {
    return '';
  }

  const formattedDay = String(day).padStart(2, '0');
  const formattedMonth = String(month).padStart(2, '0');

  return `${year}-${formattedMonth}-${formattedDay}`;
};

/**
 * Validates if a string is a valid French date format (DD/MM/YYYY)
 */
export const isValidDateFr = (frenchDate: string): boolean => {
  if (!frenchDate || typeof frenchDate !== 'string') {
    return false;
  }

  // Must match exactly DD/MM/YYYY
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(frenchDate)) {
    return false;
  }

  const parts = frenchDate.split('/');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const date = new Date(year, month - 1, day);
  
  return (
    date.getDate() === day &&
    date.getMonth() === month - 1 &&
    date.getFullYear() === year
  );
};

/**
 * Formats an ISO date string for HTML date input (YYYY-MM-DD)
 */
export const formatDateForInput = (isoDate: string): string => {
  if (!isoDate || typeof isoDate !== 'string') {
    return '';
  }

  const date = new Date(isoDate);
  
  if (isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};
